import React from 'react';
import { Box, Checkbox, Chip, FormHelperText, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import ChecklistRtlIcon from '@mui/icons-material/ChecklistRtl';
import { HOUSEKEEPING_ACCENT } from '../../utils/housekeepingMobileLayout';

const CATEGORY_LABELS = {
  bedroom: 'Bedroom',
  bathroom: 'Bathroom',
  living: 'Living / amenities',
  general: 'General',
};

export function checkedIdsFromCleaning(raw) {
  if (!Array.isArray(raw)) return new Set();
  return new Set(raw.map((itemId) => String(itemId)).filter(Boolean));
}

export function checklistStateFromCleaning(cleaning) {
  const snapshot = Array.isArray(cleaning?.checklist_snapshot) ? cleaning.checklist_snapshot : [];
  const items = normalizeChecklistItems(snapshot);
  if (items.length) {
    const fromFlags = snapshot
      .filter((item) => item && typeof item === 'object' && item.checked && item.id)
      .map((item) => String(item.id));
    return {
      items,
      checkedIds: fromFlags.length ? new Set(fromFlags) : checkedIdsFromCleaning(cleaning?.checklist_item_ids),
    };
  }
  return {
    items: [],
    checkedIds: checkedIdsFromCleaning(cleaning?.checklist_item_ids),
  };
}

export function pickRichestOpenChecklistState(cleanings = [], excludeId = null) {
  let best = { items: [], checkedIds: new Set() };
  cleanings.forEach((cleaning) => {
    if (excludeId != null && String(cleaning?.id) === String(excludeId)) return;
    const status = String(cleaning?.status || '').toUpperCase();
    if (status !== 'PENDING' && status !== 'IN_PROGRESS') return;
    const state = checklistStateFromCleaning(cleaning);
    if (state.checkedIds.size > best.checkedIds.size) best = state;
  });
  return best;
}

export function alignCheckedIdsToItems(items = [], checkedIds, savedItems = []) {
  const source = checkedIds instanceof Set ? checkedIds : checkedIdsFromCleaning(checkedIds);
  const next = new Set();
  const checkedLabels = new Set(
    (savedItems || [])
      .filter((item) => source.has(item.id))
      .map((item) => String(item.label || '').trim().toLowerCase())
      .filter(Boolean)
  );
  items.forEach((item) => {
    if (source.has(item.id) || checkedLabels.has(String(item.label || '').trim().toLowerCase())) {
      next.add(item.id);
    }
  });
  if (!next.size) {
    source.forEach((itemId) => next.add(itemId));
  }
  return next;
}

export function normalizeChecklistItems(raw, { defaultMandatory = false } = {}) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item, index) => {
      if (typeof item === 'string') {
        const label = item.trim();
        return label
          ? { id: `item-${index}`, label, category: 'general', mandatory: defaultMandatory }
          : null;
      }
      const label = String(item?.label || '').trim();
      if (!label) return null;
      return {
        id: String(item.id || `item-${index}`),
        label,
        category: String(item.category || 'general'),
        mandatory: typeof item.mandatory === 'boolean' ? item.mandatory : defaultMandatory,
      };
    })
    .filter(Boolean);
}

export function requiredChecklistItems(items = []) {
  return items.filter((item) => item.mandatory);
}

export function areRequiredChecklistItemsChecked(items = [], checkedIds) {
  return requiredChecklistItems(items).every((item) => checkedIds.has(item.id));
}

export function cleaningStatusFromChecklist({
  checklistEnabled = false,
  items = [],
  checkedIds,
} = {}) {
  if (!checklistEnabled || !items.length) return null;
  const ids = checkedIds instanceof Set ? checkedIds : checkedIdsFromCleaning(checkedIds);
  const required = requiredChecklistItems(items);
  const tracked = required.length ? required : items;
  const checkedAny = items.some((item) => ids.has(item.id));
  if (tracked.length && tracked.every((item) => ids.has(item.id))) return 'COMPLETED';
  if (checkedAny) return 'IN_PROGRESS';
  return 'PENDING';
}

export function cleaningStatusLabel(status) {
  if (status === 'IN_PROGRESS') return 'In Progress';
  if (status === 'COMPLETED') return 'Completed';
  return 'Pending';
}

/** Live checklist only when enabled. Captured items are view-only history. */
export function shouldShowCleaningChecklist({
  checklistEnabled = false,
  isViewMode = false,
  capturedItems = [],
} = {}) {
  if (checklistEnabled) return true;
  return Boolean(isViewMode && capturedItems.length);
}

export default function HousekeepingCleaningChecklist({
  items = [],
  checkedIds,
  onToggle,
  isDark = false,
  error = '',
  disabled = false,
  status = '',
}) {
  const grouped = items.reduce((acc, item) => {
    const key = CATEGORY_LABELS[item.category] ? item.category : 'general';
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});
  const requiredItems = requiredChecklistItems(items);
  const requiredCount = requiredItems.length;
  const requiredDone = requiredItems.filter((item) => checkedIds.has(item.id)).length;
  const checkedCount = items.filter((item) => checkedIds.has(item.id)).length;
  const allRequiredDone = requiredCount === 0 || requiredDone === requiredCount;
  const allDone = items.length > 0 && checkedCount === items.length;
  const progressStatus = status || cleaningStatusFromChecklist({
    checklistEnabled: true,
    items,
    checkedIds,
  });
  const progressChipSx = {
    PENDING: { bgcolor: alpha('#f59e0b', isDark ? 0.22 : 0.16), color: isDark ? '#fbbf24' : '#b45309' },
    IN_PROGRESS: { bgcolor: alpha('#29b6f6', isDark ? 0.22 : 0.16), color: isDark ? '#7dd3fc' : '#0369a1' },
    COMPLETED: { bgcolor: alpha(HOUSEKEEPING_ACCENT, 0.22), color: isDark ? HOUSEKEEPING_ACCENT : '#0f766e' },
  }[progressStatus] || {};

  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 2,
        border: `1px solid ${
          error
            ? alpha('#ef4444', 0.45)
            : isDark
              ? 'rgba(255,255,255,0.08)'
              : alpha(HOUSEKEEPING_ACCENT, 0.22)
        }`,
        bgcolor: isDark ? 'rgba(255,255,255,0.03)' : alpha(HOUSEKEEPING_ACCENT, 0.05),
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
        <ChecklistRtlIcon sx={{ fontSize: 18, color: HOUSEKEEPING_ACCENT }} />
        <Typography sx={{ fontWeight: 800, fontSize: '0.88rem', flex: 1 }}>
          Cleaning checklist
        </Typography>
        {progressStatus ? (
          <Chip
            size="small"
            label={cleaningStatusLabel(progressStatus)}
            sx={{ height: 22, fontWeight: 700, ...progressChipSx }}
          />
        ) : null}
        <Chip
          size="small"
          label={requiredCount ? `${requiredCount} required` : 'All optional'}
          sx={{
            height: 22,
            fontWeight: 700,
            bgcolor: requiredCount
              ? alpha('#f59e0b', isDark ? 0.22 : 0.16)
              : alpha(HOUSEKEEPING_ACCENT, isDark ? 0.2 : 0.16),
            color: requiredCount ? (isDark ? '#fbbf24' : '#b45309') : (isDark ? HOUSEKEEPING_ACCENT : '#0f766e'),
          }}
        />
        <Chip
          size="small"
          label={requiredCount ? `${requiredDone}/${requiredCount} required` : `${checkedCount}/${items.length}`}
          sx={{
            height: 22,
            fontWeight: 700,
            bgcolor: allDone ? alpha(HOUSEKEEPING_ACCENT, 0.22) : (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.06)'),
          }}
        />
      </Box>

      {Object.entries(grouped).map(([category, categoryItems]) => (
        <Box key={category} sx={{ mb: 1 }}>
          {Object.keys(grouped).length > 1 ? (
            <Typography
              sx={{
                fontSize: '0.68rem',
                fontWeight: 800,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: isDark ? 'rgba(226,232,240,0.55)' : '#64748b',
                mb: 0.5,
              }}
            >
              {CATEGORY_LABELS[category] || category}
            </Typography>
          ) : null}
          {categoryItems.map((item) => {
            const checked = checkedIds.has(item.id);
            return (
              <Box
                key={item.id}
                onClick={() => !disabled && onToggle(item.id)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 0.5,
                  py: 0.15,
                  borderRadius: '10px',
                  cursor: disabled ? 'default' : 'pointer',
                  '&:hover': disabled ? undefined : { bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(15,23,42,0.04)' },
                }}
              >
                <Checkbox
                  size="small"
                  checked={checked}
                  disabled={disabled}
                  onChange={() => onToggle(item.id)}
                  onClick={(event) => event.stopPropagation()}
                  sx={{
                    color: isDark ? 'rgba(255,255,255,0.35)' : '#94a3b8',
                    '&.Mui-checked': { color: HOUSEKEEPING_ACCENT },
                  }}
                />
                <Typography
                  sx={{
                    flex: 1,
                    fontSize: '0.86rem',
                    fontWeight: checked ? 600 : 500,
                    textDecoration: checked ? 'line-through' : 'none',
                    color: checked
                      ? (isDark ? 'rgba(226,232,240,0.5)' : '#64748b')
                      : (isDark ? '#e2e8f0' : '#0f172a'),
                  }}
                >
                  {item.label}
                  {item.mandatory ? ' *' : ''}
                </Typography>
                {item.mandatory ? (
                  <Chip
                    size="small"
                    label="Req"
                    sx={{
                      height: 18,
                      fontSize: '0.62rem',
                      fontWeight: 800,
                      bgcolor: alpha('#f59e0b', isDark ? 0.22 : 0.16),
                      color: isDark ? '#fbbf24' : '#b45309',
                    }}
                  />
                ) : null}
              </Box>
            );
          })}
        </Box>
      ))}

      {requiredCount > 0 && !allRequiredDone && !disabled ? (
        <Typography sx={{ mt: 0.5, fontSize: '0.75rem', color: isDark ? '#fbbf24' : '#b45309', fontWeight: 600 }}>
          Check every required item before marking the room clean.
        </Typography>
      ) : null}
      {error ? (
        <FormHelperText error sx={{ mx: 0, mt: 0.5 }}>
          {error}
        </FormHelperText>
      ) : null}
    </Box>
  );
}
